<!--

Copyright Paul B. Lucas (C) 2025, 2026   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is the initial vue app element mounted in the public html index.  The Vue-msal
authentication components are mounted here and provide the user and config Vuex store settings
for the application.  

Revisions:
    10-25-2023      pblucas     initial creation from RWA template
    06-25-2025      pblucas     Modified initial process to fetch employee from database first, 
                                then from ArchiBus EM table.  
    03-16-2026      pblucas     Review & fix bugs
    03-25-2026      pblucas     Added exDate from useAuthAPI to check gat expiry


Process flow desription:
  On entry, authentication token is checked and if not available, redirection to AD 
  login page is performed.  On redirect re-entry, claims are available.  These include:

        "groups": [
          "36188dc6-1d94-451a-a268-2ca09e5ed5f1"    <<-- administrator group
        ],
        "name": "                   <<-- 'name-concat' in store        
        "nonce":                    <<-- ignore
        "oid":                      <<-- 'ms-object-id' in store
        "preferred_username":       <<-- contact - email address in store
        "rh":                       <<-- ignore (first part of access token)
        "sub":                      <<-- ignore
        "tid":                      <<-- ignore
        "uti":                      <<-- ignore
        "ver":                      <<-- ignore (version of graph)
        "uin":                      <<-- 'uin' in store
        "jobtitle":                 <<-- 'title' in store
        "department":               <<-- 'college' in store
        "givenName":                <<-- 'first-name' in store
        "officePhone":              <<-- contact- work phone
        "surname":                  <<-- 'last-name' in store

    Note:  'home-org' not available in claims (available as 'em_id' in ArchiBus EM table)

    NetID is stripped from email claim for use as 'netid' in store
    Security group membership is tested. See configuration/adminGroups.config.json
    Administrative flags are set on group claims.
    
    Employee database is searched on netid for employee, if available, store loaded.
    If not, the ArchiBus EM table is searched on netid as 'net_user_name'

    Stores (user and config) are tested on uuid value and if null signature, local
    browser storage is tested for reload from database.  This allows SPA page to work
    after browser refresh.

    Further note:
    To support sys admin page to be able to add users to security groups, graph is called.
    However adding a user requires user's ms-object-id.  This set on new users automatically but
    may not be available on database for preloads.  Employee imports from EM in ListEmployees
    also make a graph call to obtain this OID.

  ROLE SUPPORT:
    'User':       Show Home, Request Loan, Me,  My Agreements, My Assets, About
    'Admin':      User plus Asets, Agreements, Employees
    'SysAdmin':   Admin plus Settings
    'Receiver':   Create/Build Asset
    'IT SUpport': Deploy Assets
    'Auditor':    Assets, Agreements

-->
<template>
  <div class="_main_container">
    <!-- display the persistent header -->
    <!-- <RWAHeader :unit="unit"/> -->
    <UIUCHeader :unit="unit" :unitLink="unitLink" />
    <div class="_nav_selection" v-if="bLoginCompleted"> 
      <!-- if user has multiple roles, present role selector -->
      <div class="_role_selection" v-if="hasMultipleRoles">
         <div class="_row">
          <div class="_col_40">
            <label class="_input_label" for="_role_id">Select Your Role</label>
          </div>
          <div class="_col_60">
            <select id="_role_id" class="_select" v-model="activeRole" @change="setUserActiveRole" >
                <option id="_role_id" class="_type_list" v-for="(item, idx) in aRoles" 
                    tabindex = "0"
                    aria-label="Application role selector" 
                    aria-description="Dropdown list to select current role for multi-role users"
                    :value="item"> {{item}} 
                </option>
            </select>
          </div>
        </div>
      </div>
      <nav class="_n1">
          <div v-if="!isAuthenticated && isAvailable" class="_nav_options">
            <!-- unauthenticated users see only home|about   -->
            <router-link :to="{name: 'home'}" 
                aria-label="Home navigation" 
                aria-describedby="Navigation to home page">
                Home 
            </router-link> |
            <router-link :to="{name: 'about'}" 
                aria-label="About navigation" 
                arai-describedby="Navigation to about page and app history">
                About
            </router-link> 
          </div>

          <!--added to trap API network errors -->
          <div v-if="!isAuthenticated && !isAvailable" class="_nav_options">
            <!-- system not available   -->
            <p class="_warning">
              System Not Available 
            </p>
            <p class="_p_unavail">
              The asset management API is experiencing network errors.
              An Azure system administrator is required.  Please contact one using the following email: 
              <b>ccf-admin@illinois.edu.</b>  
            </p>
          </div>

          <div class="_nav_options" v-if="isAuthenticated && activeRole == 'User'" :key="activeRole">
              <!-- users see Home/Start an Equipment Load Request/Me/My Agreements/My Assets/About-->
              <router-link :to="{name: 'home'}" 
                  aria-label="Home navigation" 
                  aria-describedby="Navigation to home page">
                  Home 
              </router-link> |
              <router-link :to="{name: 'requestLoan'}" 
                  aria-label="Request equipment loan navigation" 
                  aria-describedby="Navigation to request form">
                  Start an Equipment Loan Request 
              </router-link> |
              <router-link :to="{name: 'viewMyData'}" 
                  aria-label="My personal data"  
                  aria-describedby="Navigation to my personal data">
                  Me 
              </router-link> |
              <router-link :to="{name: 'listMyAgreements'}" 
                  aria-label="My agreements navigation"  
                  aria-describedby="Navigation to any user agreements">
                  My Agreements 
              </router-link> |
              <router-link :to="{name: 'listMyAssets'}" 
                  aria-label="My assets navigation" 
                  aria-describedby="Navigation to list of user's deployed assets">
                  My Assets 
              </router-link> | 
              <router-link :to="{name: 'about'}" 
                  aria-label="About navigation" 
                  arai-describedby="Navigation to about page and app history">
                  About 
              </router-link> |
              <router-link :to="{name: 'logout'}" 
                  aria-label="Louout of application" 
                  arai-describedby="Logout navigation option">
                  Logout 
              </router-link> 
         </div>
          <div class="_nav_options" v-if="isAuthenticated && activeRole == 'Receiver'" :key="activeRole">
              <!-- Receivers see Home/Me/Create an Asset/Assets/About-->
              <!-- Edit privilege on FabWeb+ asset properties -->
              <router-link :to="{name: 'home'}" 
                aria-label="Home navigation" 
                aria-describedby="Navigation to home page">
                Home 
              </router-link> |
              <router-link :to="{name: 'viewMyData'}" 
                  aria-label="My personal data"  
                  aria-describedby="Navigation to my personal data">
                  Me 
              </router-link> |
              <router-link :to="{name: 'createAsset'}" 
                  aria-label="Create a new asset in inventory system"  
                  aria-describedby="Navigation to create a new asset in inventory system">
                  Create a New Asset Entry 
              </router-link> |
              <router-link :to="{name: 'listAssets' }" 
                  aria-label="Asset list navigation" 
                  aria-describedby="Navigation for administrative access to unit's deployed assets">
                  Assets 
              </router-link> |
              <router-link :to="{name: 'about'}" 
                  aria-label="About navigation" 
                  arai-describedby="Navigation to about page and app history">
                  About 
              </router-link> |
              <router-link :to="{name: 'logout'}" 
                  aria-label="Louout of application" 
                  arai-describedby="Logout navigation option">
                  Logout 
              </router-link> 
          </div>
          <div class="_nav_options" v-if="isAuthenticated && activeRole == 'IT Support'" :key="activeRole">
              <!-- IT Support see Home/Me/Deploy an Asset/Recover an Asset/Assets/About-->
              <!-- Edit privilege on some steward and asset audit properties -->
              <router-link :to="{name: 'home'}" 
                aria-label="Home navigation" 
                aria-describedby="Navigation to home page">
                Home 
              </router-link> |
              <router-link :to="{name: 'viewMyData'}" 
                  aria-label="My personal data"  
                  aria-describedby="Navigation to my personal data">
                  Me 
              </router-link> |
              <router-link :to="{name: 'deployAssets' }" 
                  aria-label="Assign asets to personnel" 
                  aria-describedby="Navigation to asset deployment to personnel">
                  Deploy Assets 
              </router-link> |
              <router-link :to="{name: 'recoverAssets' }" 
                  aria-label="Asset list navigation" 
                  aria-describedby="Navigation for administrative access to unit's deployed assets">
                  Recover Assets 
              </router-link> |
              <router-link :to="{name: 'listAssets' }" 
                  aria-label="Asset list navigation" 
                  aria-describedby="Navigation for administrative access to unit's deployed assets">
                  Assets 
              </router-link> |
              <router-link :to="{name: 'disposition' }" 
                  aria-label="Asset disposition" 
                  aria-describedby="Navigation for dispoistion (inventory or surplus) of assets">
                  Disposition 
              </router-link> |
             <router-link :to="{name: 'about'}" 
                  aria-label="About navigation" 
                  arai-describedby="Navigation to about page and app history">
                  About 
              </router-link> |
              <router-link :to="{name: 'logout'}" 
                  aria-label="Louout of application" 
                  arai-describedby="Logout navigation option">
                  Logout 
              </router-link> 
          </div>
          <div class="_nav_options" v-if="isAuthenticated && activeRole == 'Auditor'" :key="activeRole">
              <!-- Auditors see Home/Assets/About-->
              <!-- Edit privilege on some asset properties -->
              <router-link :to="{name: 'home'}" 
                aria-label="Home navigation" 
                aria-describedby="Navigation to home page">
                Home 
              </router-link> |
              <router-link :to="{name: 'listAuditableAssets' }" 
                aria-label="Asset list navigation" 
                aria-describedby="Navigation for audit access to unit's deployed assets">
                Auditable Assets 
            </router-link> |
            <router-link :to="{name: 'about'}" 
                aria-label="About navigation" 
                arai-describedby="Navigation to about page and app history">
                About 
            </router-link> |
            <router-link :to="{name: 'logout'}" 
                aria-label="Louout of application" 
                arai-describedby="Logout navigation option">
                Logout 
            </router-link> 
          </div>
          <div class="_nav_options" v-if="isAuthenticated && activeRole == 'Dept Admin'" :key="activeRole">
              <!-- Dept Admins see Home/Me/Agreements/Employees/Assets (by orgcode)/About-->
              <router-link :to="{name: 'home'}" 
                aria-label="Home navigation" 
                aria-describedby="Navigation to home page">
                Home 
              </router-link> |
              <router-link :to="{name: 'viewMyData'}" 
                  aria-label="My personal data"  
                  aria-describedby="Navigation to my personal data">
                  Me 
              </router-link> |
              <router-link :to="{name: 'listAgreements'}" 
                  aria-label="Agreement list naviagation" 
                  aria-describedby="Navigation for administrative access to unit's agreements">
                  Agreements 
              </router-link> |
              <router-link :to="{name: 'listAssets' }" 
                  aria-label="Asset list navigation" 
                  aria-describedby="Navigation for administrative access to unit's deployed assets">
                  Assets 
              </router-link> |
              <router-link :to="{name: 'unitDeploy' }" 
                  aria-label="Unit asset deployment" 
                  aria-describedby="Navigation for administrative access to unit asset deployment">
                  Unit Asset Deployment 
              </router-link> |
              <router-link :to="{name: 'unitRecover' }" 
                  aria-label="Unit asset recovery" 
                  aria-describedby="Navigation for administrative access to unit asset recovery">
                  Unit Asset Recovery 
              </router-link> |
              <router-link :to="{name: 'disposition' }" 
                  aria-label="Unit asset disposition" 
                  aria-describedby="Navigation for administrative unit asset disposition">
                  Asset Disposition 
              </router-link> |
              <router-link :to="{name: 'listEmployees' }" 
                  aria-label="List employees navigation" 
                  aria-describedby="Navigation for administrative access to organization's employees">
                  Employees 
              </router-link> |       
              <router-link :to="{name: 'about'}" 
                  aria-label="About navigation" 
                  arai-describedby="Navigation to about page and app history">
                  About 
              </router-link> |
              <router-link :to="{name: 'logout'}" 
                  aria-label="Louout of application" 
                  arai-describedby="Logout navigation option">
                  Logout 
              </router-link> 
          </div>
          <div class="_nav_options" v-if="isAuthenticated && activeRole == 'System Admin'" :key="activeRole">
              <!-- Dept Admins see Home/Me/Agreements/Employees/Assets/Settings/About-->
              <router-link :to="{name: 'home'}" 
                aria-label="Home navigation" 
                aria-describedby="Navigation to home page">
                Home 
              </router-link> |
              <router-link :to="{name: 'viewMyData'}" 
                  aria-label="My personal data"  
                  aria-describedby="Navigation to my personal data">
                  Me 
              </router-link> |
              <router-link :to="{name: 'listAgreements'}" 
                  aria-label="Agreement list naviagation" 
                  aria-describedby="Navigation for administrative access to unit's agreements">
                  Agreements 
              </router-link> |
              <router-link :to="{name: 'listAssets' }" 
                  aria-label="Asset list navigation" 
                  aria-describedby="Navigation for administrative access to unit's deployed assets">
                  Assets 
              </router-link> |
              <router-link :to="{name: 'listEmployees' }" 
                  aria-label="List employees navigation" 
                  aria-describedby="Navigation for administrtive access to organization's employees">
                  Employees 
              </router-link> |       
              <router-link :to="{name: 'settings' }" 
                  aria-label="System settings navigation (gear icon)" 
                  aria-describedby="Navigation icon for administrative access to system configuration settings.">
                  <img class="tools" alt="settings gear" src="./assets/settings.png"> 
              </router-link> |
              <router-link :to="{name: 'about'}" 
                  aria-label="About navigation" 
                  arai-describedby="Navigation to about page and app history">
                  About 
              </router-link> |
              <router-link :to="{name: 'logout'}" 
                  aria-label="Louout of application" 
                  arai-describedby="Logout navigation option">
                  Logout 
              </router-link> 
          </div>
      </nav>
    </div>
    <!-- document content pages -->
    <div v-if="bLoginCompleted" class="doc-content">
      <router-view v-slot="{ Component }">
          <component :is="Component" :claims="claims" :gat="graphAccessToken"/>
      </router-view>

    </div>
    <UIUCFooter />
  </div> 

</template>

<script>
    import { ref, computed }  from 'vue'
    import { useStore }       from 'vuex'
    
    // composables 
    import useConfigAPI       from '@/composables/useConfigAPI'
    import useEmployeesAPI    from '@/composables/useEmployeesAPI'
    import useAuthAPI         from '@/composables/useAuthAPI'
    import usePermissionsAPI  from '@/composables/usePermissionsAPI'
    import useArchiBusAPI     from '@/composables/useArchiBusAPI'

    // components
    import UIUCHeader         from '@/components/UIUCHeader.vue'
    import UIUCFooter         from '@/components/UIUCFooter.vue'

    export default {
      components: {
        UIUCHeader,
        UIUCFooter
      },
      props: [
      ],
      emits: [
      ],
      
      setup() {

   
        const { emplResults, emplKeyData, fetchEmployeesOnQuery, storeEmployee }    = useEmployeesAPI()  
        const { configResults, fetchConfigsOnQuery}                                 = useConfigAPI()
        const { handleAuthentication, authState, graphAccessToken, expireDate }     = useAuthAPI()  
        const { checkAdminGroups }                                                  = usePermissionsAPI() 
        const { mapTemplatedEMEmployee, mappedEMEmplTemplate }                      = useArchiBusAPI()
        const store                                                                 = useStore()

        // auth/login data
        const bLoginCompleted   = ref(false)

        //  store data
        const user                  =   computed( () => store.state.user)
        const config                =   computed( () => store.state.config)
        const systemAdministrator   =   computed( () => store.state.user['is-sys-admin'])
        const deptAdministrator     =   computed( () => store.state.user['is-dept-admin'])
        const defaultRole           =   computed( () => store.state.user['default-role'])
        const claims                =   ref({})
        const storableEmployee      =   ref({})
  
        const aRoles                =   computed( () =>   {
            let a = []
            a.push("User")      // always a user
            if( store.state.user['is-receiver'])    a.push("Receiver")
            if( store.state.user['is-auditor'])     a.push("Auditor")
            if( store.state.user['is-it-support'])  a.push("IT Support")
            if( store.state.user['is-sys-admin'])   a.push("System Admin")
            if( store.state.user['is-dept-admin'])  a.push("Dept Admin")
            return a
        })
 
        const hasMultipleRoles    = computed( () =>   {
          let b = false
          if(aRoles.value.length > 1) b = true
          return b
        })

        // set active role to default
        const activeRole            =   ref("User")
        localStorage.setItem("active-role", activeRole.value)

        //  actions on store
        const setUser                   =   (employee) => store.dispatch('setUser', employee)
        const setConfig                 =   (config)   => store.dispatch('setConfig', config)
 
        const unit                      =   computed( () => store.state.config.title)
        const unitLink                  =   computed( () => store.state.config['title-link'])
        
        const isAuthenticated           =   ref(false)          // basic authentication status
        const isAvailable               =   ref(false)          // API responding properly
        const tokenExpireDate           =   ref()
          
        // Note:  if first in as unauthorized, will not return from this function call.
        //        Will instead redirect to University Authentication page
        //        handleAuthentication is async
        //        -- awaits initialization
        //        -- handlesRedirectPromise asynchronously
        //        -- if user state is valid
        //        ---- gets user groups asynchronously
        //        ---- if results from graph available sets securityGroups to return
        //        --------- await response

        handleAuthentication()
        .then( async () =>  {
          // check MSAL object state
          if(authState.user != null) {

              // non admin users are authorized if they authenticate
              isAuthenticated.value = true 
              claims.value          = authState.user.idTokenClaims
              tokenExpireDate.value = expireDate.value

              // DEBUG
              //console.log("App:: access token expires: "+expireDate.value.toString())

              // will set store admin privileges  - must be done AFTER store is established for user
              checkAdminGroups(claims)

              // MARK: - Process claims to get appropriate employee properties
              // generate netid from 'username' in user
              // generate UIN from 'uin' in optional claims @ user.idTolkenClaims
              let unetid  = authState.user.username.split("@")[0]
              let uuin    = claims.uin

              // set local storage for netid (reloads user into store allowing refresh of pages)
              localStorage.setItem("user-netid", unetid)

              // MARK:  using claims netid attempt to find user in local API database
              await fetchEmployeesOnQuery({"netid": unetid})
              .then( async () => {
                if(emplResults.value.length != 0) {
                    
                    isAvailable.value = true
                    // employee found in database
                    setUser(emplResults.value[0])
                    // will set store admin privileges  - must be done AFTER store is established for user
                    checkAdminGroups(claims.value)
                    setUserConfig(user.value)
                    // set the active role
                    let r  = localStorage.getItem("active-role")
                    if( r == 'undefined') activeRole.value = "User"
                    else activeRole.value = r
                    bLoginCompleted.value = true   

                    // debug
                    //console.log("App:: setup:  in-db: store.user: "+JSON.stringify(user.value))
                }
                // else user is not in database  - try EM table
                else {
                  // EM mapping takes query on netid to EM and maps result to empl object
                  await mapTemplatedEMEmployee(claims.value)
                  .then( async() => {
                      // test for object 
                      if( mappedEMEmplTemplate.value)   {
                          // employee in EM table
                          // pull contact office phone from claims
                          if(claims.value.hasOwnProperty('officePhone')) {
                              let contactTemplate         =   {
                                "type":     "work telephone",
                                "value":    claims.value.officePhone
                              }
                              mappedEMEmplTemplate.value['contacts'].push(contactTemplate)    
                          }
 
                          // de-ref
                          storableEmployee.value = JSON.parse(JSON.stringify(mappedEMEmplTemplate.value))

                          // load into database
                          await storeEmployee(mappedEMEmplTemplate.value)
                          .then( async () => {
                              const results = emplKeyData.value.results
                              if(results?.length > 0) { 
                                  // API is available 
                                  isAvailable.value = true
                                  if(!results?.[0]?.message?.includes('success'))  { 
                                    console.log("App:: setup: failure to store new employee")
                                  } 
                                  // refetch to obtain UUID and image defaults - generated in employee constructor
                                  await fetchEmployeesOnQuery({"id": results?.[0]?.pkey})
                                  .then( () => {
                                      if(emplResults.value.length != 0) {
                                          // update store
                                          setUser(emplResults.value[0])
                                          // will set store admin privileges  - must be done AFTER store is established for user
                                          checkAdminGroups(claims.value)
                                          setUserConfig(user.value)
                                          // set the active role
                                          let r  = localStorage.getItem("active-role")
                                          if( r == 'undefined') activeRole.value = "User"
                                          else activeRole.value = r
                                          bLoginCompleted.value = true                             
                                      }
                                  })
                              }
                              else {
                                isAvailable.value     = false
                                isAuthenticated.value = false
                                bLoginCompleted.value = true  
                              }                           
                            })   
                      }
                      else {
                          // not in EM table - possible ArchiBus/iCard sync error
                          // user still authenticated to AD thought so create user
                          // use claims data to set up user on minimum template
                          let defaultEmployee = {
                              'name-concat':            "",
                              'first-name':             claims.value.givenName,
                              'last-name':              claims.value.surname,
                              'middle-initial':         "",
                              'netid':                  unetid,
                              'ms-object-id':           claims.value.oid,
                              'uin':                    uuin,
                              'home-org':               "",
                              'eclass-code':            "",
                              'title':                  claims.value.jobtitle,
                              'address1':               "",
                              "address2":               "",
                              "city":                   "",
                              "state":                  "",
                              "postcode":               "",
                              "country":                "United States",
                              "mailcode":               "",
                              "timezone":               "America/Chicago",        // default cst/cdt
                              "req-photo-suppress":     false,
                              "college":                claims.value.department,  // college returned as department on claims
                              "college-code":           null,
                              "department":             null,
                              "department-code":        null,
                              "division":               null,
                              'division-code':          null,
                      
                              // the following user properties will be updated by membership in Entra security groups
                              // these are mutated on store.state.user AFTER user is update with new Employee
                              'is-sys-admin':           false,
                              'is-dept-admin':          false,
                              // additional security constraints
                              // must be added by admin on employee record
                              'admins-multiple-orgs':   false,
                              'allowed-orgs':           [],  
                              // pull known contacts from claims   
                              'contacts':               []
                          }

                          // pull contact office phone from claims
                          if(claims.value.hasOwnProperty('officePhone')) {
                              let contactTemplate         =   {
                                "type":     "work telephone",
                                "value":    claims.value.officePhone
                              }
                              defaultEmployee['contacts'].push(contactTemplate)   
                          }
                          // do principal email
                          if(claims.value.hasOwnProperty('preferred_username')) {
                              let contactTemplate         =   {
                                "type":     "email",
                                "value":    claims.value.preferred_username
                              }
                              defaultEmployee['contacts'].push(contactTemplate)   
                          }
                                                  
                          // load into database
                          await storeEmployee(defaultEmployee)
                          .then( async () => {
                              const results = emplKeyData.value.results
                              if(results?.length > 0) { 
                                // API is available 
                                isAvailable.value = true
                                if(!results?.[0]?.message?.includes('success'))  { 
                                  console.log("App:: setup: failure to store new employee")
                                } 
                                await fetchEmployeesOnQuery({"id": results?.[0]?.pkey})
                                .then( () => {
                                    if(emplResults.value.length != 0) {
                                        // update store
                                        setUser(emplResults.value[0])
                                        // will set store admin privileges  - must be done AFTER store is established for user
                                        checkAdminGroups(claims.value)
                                        setUserConfig(user.value)
                                        let r  = localStorage.getItem("active-role")
                                        if( r == 'undefined') activeRole.value = "User"
                                        else activeRole.value = r
                                        bLoginCompleted.value = true                             
                                    }
                                })
                              }
                              else {
                                isAvailable.value     = false
                                isAuthenticated.value = false
                                bLoginCompleted.value = true  
                              }  
                          })
                      }
                  })
                }  // end else case - no user in database
              }) // end thenable on employee fetch
          }
          else {
              // user is NOT AUTHENTICATED
              localStorage.removeItem("user-netid")   // no auth user
              isAvailable.value     = false
              isAuthenticated.value = false
              bLoginCompleted.value = true  
          }     
        }) // end thenable on handleAuthentication

        
        // SUPPORT FUNCTIONS

        // FUNCTION:  set initial employee unit config
        const setUserConfig = (user) =>  {
          // MARK: - Set configuration based on employee homeOrg
          if(user['home-org'])  {
              // home org exists - take frist three digits as code
              fetchConfigsOnQuery({"apply-orgcode": user['home-org'].substr(0,3)})
              .then( () => {
                  // null object "{}" has length 2
                  if(JSON.stringify(configResults.value).length > 2) {
                      // config found in RWA database
                      // update store
                      setConfig(configResults.value[0])   
                      localStorage.setItem("config-uuid", configResults.value[0].uuid)  
                      //console.log("App:: setup: setUserConfig: config: "+JSON.stringify(configResults.value[0]))
                  }
              })
          }
        }
        return { unit, unitLink, config, isAuthenticated, isAvailable, authState, bLoginCompleted, activeRole, 
                 systemAdministrator, deptAdministrator, graphAccessToken, hasMultipleRoles, defaultRole, aRoles,
                 claims, storableEmployee, tokenExpireDate}
    },

    
    data () {
      return {}
    },
    methods: {
      setUserActiveRole() {
        localStorage.setItem("active-role", this.activeRole)
        this.$router.push({name: "home"}) 
        
      }
    },
    // hooks
    beforeMount() {
     
    },

    mounted()   {
 
    },
  
    updated()   {
      //console.log("APP:: exec: activeRole: "+this.activeRole)
      //console.log("App:: updated: bLoginCompleted: "+this.bLoginCompleted)
      //console.log("App:: updated: isAuthenticated: "+this.isAuthenticated)      
      //console.log("App:: updated: hasMultipleRoles: "+this.hasMultipleRoles)      
      //console.log("App:: updated: activeRole: "+this.activeRole)      
      //console.log("App:: updated: isAvailable: "+this.isAvailable)    
      //console.log("App:: updated: access token expires on: "+this.tokenExpireDate.toString())

    }

 
  }
  
</script>

<style>
    
    #app {
      font-family: Monserrat, Avenir, Helvetica, Arial, sans-serif;
      margin: 0;
    }
    body {
      margin: 0;
      padding: 0;
    }
    ._main_container {
      width:  100%;
      scrollbar-gutter: stable both-edges; 
      margin: 0 auto;
    }
    .spinner_container {
      width: 40%;
      margin: 200px auto;
    }
    .spinner {
      position: relative; 
      margin: auto;
    }
    ._nav_selection {
      margin: 0;
      padding: 0;
    }
    ._role_selection {
      width: 60%;
      float: right;
      display: flex;
      flex-direction: column;
      align-items: right;
      justify-content: flex-start;
      margin: 0 0 15px 0;
      padding: 0;
    }
    ._p_loading {
      font-size: 18px;
      color: blue;
      text-align: center;
    }
    ._p_unavail {
      width: 50%;
      font-size: 14px;
      color: black;
      text-align: justify;
      margin: 20px auto;
    }
    ._warning {
      font-size: 16px;
      font-weight: bold;
      color: black;
      text-align: center;
    }
    .doc-content {

      width: 100%;
      max-height: 90%;
      min-height: 50%;
    }
    ._select {
        max-width: 100%;
        margin: 0 10px 0 0;
        padding: 2px 0 0 15px;
        font-size: 0.9em;
        border-style: solid;
        border-width: 2px;
        border-radius: 4px;
        border-color: #13294B;
        background-color: #E8E9EB;
        color: #13294B;
    }
    ._type_list {
        max-width: 100%;
        margin: 0 10px 0 0;
        padding: 2px 0 0 15px;
        font-size: 0.9em;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        background-color:#E8E9EB;
        color: #13294B;
        box-sizing: border-box; 
        resize: vertical;
    }
    ._type_list:disabled {
        color: green;
        font-weight: bold;
    }
    ._input_label {
        display: inline-block;  
        color: blue;
        font-weight: bold;
        font-size: 0.8em;
        text-align: left;
        padding: 2px 0;  
    }
    ._col_40 {
        float: left;
        text-align: center;
        width: 40%;
        margin-top: 5px;
    }
 
    ._col_60 {
        float: left;
        width: 60%;
        margin-top: 5px;
    }
    ._row {
        text-align: left;
    }
    ._n1 {
      width: 100%;
      background-color: #13294B;
      /* background-color: #E8E9EB; */
      font-family: Monserrat, Avenir, Helvetica, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      min-height: 20px;
      overflow: hidden;
      padding: 4px;
      margin: 0;
    }

    a {
      font-weight: normal;
      font-size: 0.9em;
      color: #E8E9EB;
      text-decoration: none;
      padding: 4px;
      border-style: hidden;
      border-radius: 4px;
    }
    a:hover {
      color: #13294B;
      background-color: white;
    }

    a.router-link-exact-active {
      color: #13294B;
      background-color: white;
    }

    .tools {
      width: 15px;
      margin: 0;
      padding: 2px;
    }
    .tools:hover {
      margin: 0;
      padding: 2px;
      color: blue;

    }

    .app_authenticator  {
      width: 50%;
      margin:5px auto;
      padding:5px;
    }

    .app_col_50 {
      /* float: left; */
      width: 100%;
      margin: 5px;
      padding: 0;
    }

    .app_p_info {
      font-size: 0.9em;
      font-weight: bold;
      color: black;
      margin: 5px 5px 25px;
      padding: 2px;
    }
    .app_row:after {
        content: "";
        display: table;
        clear: both;
    }
    .app_scol_40 {
      float: left;
      width: 40%;
    }
    .app_scol_60 {
      float: left;
      width: 60%;
    }
    .app_item_label {
      font-size: 0.9em;
      font-weight: normal;
      color: blue;
      margin: 0;
      padding: 2px;
    }
    .app_input {
      /* width: 100%;  */
      font-size: 0.9em;
      font-weight: normal;
      color: black;
      margin: 0;
      padding: 2px;
    }
    ._nav_options {
      display: inline;
      color: white;
    }
</style>












